/**
 * Derive up-to-two-character initials from a display name.
 *
 * @remarks
 * SE-5492 extracted this rule so the player-card avatar's initials fallback is
 * not a fourth private copy. Three private `deriveInitials`/`getInitials`
 * helpers with these exact semantics already exist
 * (`features/scouting/.../MobileTargetCardContainer`,
 * `features/reports/.../ShareRecipientsListContainer`,
 * `components/organisms/shared/useShareModal`). They are intentionally NOT
 * migrated here — each sits in a different feature and this ticket's blast
 * radius is the pipeline card. Collapsing them onto this module is a
 * mechanical follow-up.
 * @module utils/playerInitials
 */

/**
 * Derive initials: first letter of the first token + first letter of the last
 * token, uppercased.
 * @remarks
 * Single-token names return the first two characters. Empty/whitespace-only
 * input falls back to `"?"` — never an empty string, so the avatar circle is
 * never blank.
 * @param displayName - The player's display name.
 * @returns Up-to-two-character uppercase initials, or `"?"`.
 */
export const derivePlayerInitials = (
  displayName: string | null | undefined
): string => {
  const parts = (displayName ?? "")
    .trim()
    .split(/\s+/)
    .filter(part => part.length > 0);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};
